import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Drawer,
  IconButton,
  Typography,
  Link,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Button,
  CircularProgress,
  Chip,
  Tooltip,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import CameraIcon from '@mui/icons-material/CameraAlt';
import PersonIcon from '@mui/icons-material/Person';
import SchoolIcon from '@mui/icons-material/School';
import AwardIcon from '@mui/icons-material/EmojiEvents';
import SettingsIcon from '@mui/icons-material/Settings';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ProfilePicture from '../assets/profile-picture.jpg';
import { updateProfilePhoto } from '../api/services';
import { useProfile } from '../context/ProfileContext';
import { AuthContext } from '../context/auth-context';

// ---------------------------------------------------------------
// Shared look (design tokens) - same as the rest of IZHub
// ---------------------------------------------------------------
const ui = {
  primary: '#009db9',
  primaryDark: '#007a8a',
  primarySoft: '#e6f6f9',
  text: '#1f2933',
  muted: '#6b7280',
  border: '#e5e7eb',
  surface: '#f7f9fb',
  success: '#2e7d32',
  successSoft: '#e8f5e9',
  shadow: '0 4px 20px rgba(16, 24, 40, 0.06)',
};

// Ids of the on-page sections the nav links scroll to (set in ProfileManager / ProfilePage)
const SECTION_USER = 'profile-section-user';
const SECTION_CERTIFICATES = 'profile-section-certificates';
const SECTION_DIPLOMAS = 'profile-section-diplomas';
const SECTION_ACCOUNT = 'profile-section-account';
const SECTION_IDS = [SECTION_USER, SECTION_CERTIFICATES, SECTION_DIPLOMAS, SECTION_ACCOUNT];

// Presentation-only helpers
const formatRole = (role) =>
  String(role)
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());

const clampProgress = (value) => Math.max(0, Math.min(100, Number(value) || 0));

// Avatar surrounded by a profile-completion ring
const ProgressAvatar = ({ imageUrl, progress, size = 120, children }) => {
  const inner = size - 16;
  return (
    <Box sx={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <CircularProgress
        variant="determinate"
        value={100}
        size={size}
        thickness={3}
        sx={{ position: 'absolute', top: 0, left: 0, color: '#e5e7eb' }}
      />
      <CircularProgress
        variant="determinate"
        value={clampProgress(progress)}
        size={size}
        thickness={3}
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          color: ui.primary,
          '& .MuiCircularProgress-circle': { strokeLinecap: 'round' },
        }}
      />
      <img
        src={imageUrl || ProfilePicture}
        alt="Profile"
        style={{
          position: 'absolute',
          top: 8,
          left: 8,
          width: inner,
          height: inner,
          borderRadius: '50%',
          objectFit: 'cover',
          border: '3px solid #fff',
          boxShadow: '0 2px 8px rgba(16, 24, 40, 0.15)',
          backgroundColor: '#fff',
        }}
      />
      {children}
    </Box>
  );
};

const ProfileContent = ({
  progress,
  imageUrl,
  handleImageChange,
  navItems,
  onNavClick,
  user,
  inputId,
  onClose,
  activeSection,
}) => {
  const completion = clampProgress(progress);

  return (
    <Box
      sx={{
        borderRadius: '20px',
        border: `1px solid ${ui.border}`,
        backgroundColor: '#fff',
        boxShadow: ui.shadow,
        overflow: 'hidden',
      }}
    >
      {/* Cover */}
      <Box
        sx={{
          height: 100,
          position: 'relative',
          background:
            'radial-gradient(circle at 20% 20%, rgba(255,255,255,0.22) 0, rgba(255,255,255,0) 45%), radial-gradient(circle at 85% 80%, rgba(255,255,255,0.16) 0, rgba(255,255,255,0) 40%), linear-gradient(135deg, #009db9 0%, #007a8a 100%)',
        }}
      >
        {onClose && (
          <IconButton
            onClick={onClose}
            aria-label="Close menu"
            size="small"
            sx={{
              position: 'absolute',
              top: 10,
              right: 10,
              color: '#fff',
              backgroundColor: 'rgba(255,255,255,0.2)',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.32)' },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      <Box sx={{ px: 3, pb: 3, mt: '-60px', textAlign: 'center' }}>
        {/* Profile Picture Section */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <ProgressAvatar imageUrl={imageUrl} progress={progress} size={120}>
            <label htmlFor={inputId}>
              <Tooltip title="Change photo" arrow>
                <IconButton
                  component="span"
                  aria-label="Change profile photo"
                  sx={{
                    position: 'absolute',
                    bottom: 2,
                    right: 2,
                    bgcolor: ui.primary,
                    p: 0.9,
                    border: '2px solid #fff',
                    borderRadius: '50%',
                    '&:hover': { bgcolor: ui.primaryDark },
                  }}
                >
                  <CameraIcon sx={{ width: 16, height: 16, color: 'white' }} />
                </IconButton>
              </Tooltip>
              <input
                type="file"
                id={inputId}
                style={{ display: 'none' }}
                accept="image/*"
                onChange={handleImageChange}
              />
            </label>
          </ProgressAvatar>
        </Box>

        {/* User Info - Display user name/email */}
        {user && (
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <Typography
              variant="h6"
              sx={{ color: ui.text, fontWeight: 700, lineHeight: 1.3, wordBreak: 'break-word' }}
            >
              {user.name || 'User'}
            </Typography>
            <Typography variant="body2" sx={{ color: ui.muted, wordBreak: 'break-all' }}>
              {user.email || ''}
            </Typography>
            {user.role && (
              <Chip
                size="small"
                label={formatRole(user.role)}
                sx={{
                  mt: 1.25,
                  fontWeight: 700,
                  borderRadius: '8px',
                  backgroundColor: ui.primarySoft,
                  color: ui.primaryDark,
                }}
              />
            )}
          </Box>
        )}

        {/* Profile completion */}
        <Box
          sx={{
            mb: 2.5,
            p: 1.75,
            borderRadius: '14px',
            backgroundColor: completion >= 100 ? ui.successSoft : ui.surface,
            border: `1px solid ${completion >= 100 ? '#c8e6c9' : ui.border}`,
            textAlign: 'left',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography
              sx={{
                fontSize: '0.75rem',
                fontWeight: 700,
                letterSpacing: 0.6,
                textTransform: 'uppercase',
                color: completion >= 100 ? ui.success : ui.primaryDark,
              }}
            >
              Profile completion
            </Typography>
            <Typography
              sx={{
                fontWeight: 800,
                color: completion >= 100 ? ui.success : ui.primary,
                fontSize: '1.05rem',
              }}
            >
              {progress}%
            </Typography>
          </Box>
          <Typography variant="caption" sx={{ color: ui.muted }}>
            {completion >= 100
              ? 'Your profile is complete'
              : 'Complete your profile to reach 100%'}
          </Typography>
        </Box>

        {/* Edit Profile Button */}
        <Button
          variant="outlined"
          fullWidth
          startIcon={<EditOutlinedIcon fontSize="small" />}
          sx={{
            borderColor: ui.primary,
            color: ui.primary,
            textTransform: 'none',
            fontSize: '0.9rem',
            fontWeight: 600,
            borderRadius: '10px',
            py: 1,
            mb: 3,
            '&:hover': { borderColor: ui.primaryDark, bgcolor: ui.primarySoft },
          }}
        >
          Edit Profile
        </Button>

        {/* Navigation Items */}
        <Typography
          sx={{
            textAlign: 'left',
            fontSize: '0.75rem',
            fontWeight: 700,
            letterSpacing: 0.6,
            textTransform: 'uppercase',
            color: ui.muted,
            mb: 1,
            px: 0.5,
          }}
        >
          User Information
        </Typography>
        <List disablePadding sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {navItems.map((item, index) => {
            const isActive = item.target === activeSection;
            return (
              <ListItemButton
                key={index}
                component={Link}
                href={`#${item.target}`}
                onClick={(event) => onNavClick(event, item.target)}
                underline="none"
                selected={isActive}
                sx={{
                  borderRadius: '12px',
                  px: 1.5,
                  py: 1,
                  textAlign: 'left',
                  '&:hover': { backgroundColor: ui.surface },
                  '&.Mui-selected': {
                    backgroundColor: ui.primarySoft,
                    '&:hover': { backgroundColor: ui.primarySoft },
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 0, mr: 1.5 }}>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '10px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: isActive ? '#fff' : ui.primarySoft,
                    }}
                  >
                    {item.icon}
                  </Box>
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{
                    variant: 'body2',
                    color: isActive ? ui.primaryDark : ui.text,
                    fontWeight: isActive ? 700 : 600,
                    letterSpacing: 0.3,
                  }}
                />
                <ChevronRightIcon
                  fontSize="small"
                  sx={{ color: isActive ? ui.primary : '#b0b7be' }}
                />
              </ListItemButton>
            );
          })}
        </List>
      </Box>
    </Box>
  );
};

const ProfileSidebar = ({ progress = 0 }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { profileImageUrl, updateProfileImage } = useProfile();
  const { user } = useContext(AuthContext);
  const [activeSection, setActiveSection] = useState(SECTION_USER);

  // Highlight the nav item for the section currently in view
  useEffect(() => {
    const elements = SECTION_IDS.map((id) => document.getElementById(id)).filter(Boolean);
    if (elements.length === 0 || typeof IntersectionObserver === 'undefined') {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: '-25% 0px -60% 0px', threshold: 0 }
    );
    elements.forEach((element) => observer.observe(element));

    // The last section can be too short to reach the observed band, so
    // treat "scrolled to the bottom" as the last section being active.
    const handleScroll = () => {
      const atBottom =
        window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 4;
      if (atBottom) {
        setActiveSection(SECTION_ACCOUNT);
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleImageChange = async (event) => {
    const file = event.target.files[0];
    if (file) {
      try {
        await updateProfilePhoto(file);
        await updateProfileImage();
      } catch (error) {
        console.error('Error updating profile photo:', error);
        if (error.response?.status === 403) {
          console.error('403 Forbidden: Check auth token or content type.');
        }
      }
    }
  };

  const navItems = [
    { icon: <PersonIcon sx={{ width: 20, height: 20, color: '#009db9' }} />, text: 'USER PROFILE', target: SECTION_USER },
    { icon: <AwardIcon sx={{ width: 20, height: 20, color: '#009db9' }} />, text: 'CERTIFICATE', target: SECTION_CERTIFICATES },
    { icon: <SchoolIcon sx={{ width: 20, height: 20, color: '#009db9' }} />, text: 'DIPLOMA', target: SECTION_DIPLOMAS },
    { icon: <SettingsIcon sx={{ width: 20, height: 20, color: '#009db9' }} />, text: 'ACCOUNT SETTING', target: SECTION_ACCOUNT },
  ];

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Scrolls to the clicked section (instead of navigating away from the page)
  const handleNavClick = (event, targetId) => {
    event.preventDefault();
    setActiveSection(targetId);
    if (isMenuOpen) {
      setIsMenuOpen(false);
      // Wait for the drawer to close so its scroll lock doesn't block the scroll
      setTimeout(() => scrollToSection(targetId), 300);
    } else {
      scrollToSection(targetId);
    }
  };

  return (
    <>
      {/* Compact profile header for mobile / tablet - opens the full menu */}
      <Box
        sx={{
          display: { xs: 'flex', lg: 'none' },
          alignItems: 'center',
          gap: 2,
          p: 2,
          borderRadius: '16px',
          border: `1px solid ${ui.border}`,
          backgroundColor: '#fff',
          boxShadow: ui.shadow,
        }}
      >
        <ProgressAvatar imageUrl={profileImageUrl} progress={progress} size={72} />
        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
          {user && (
            <>
              <Typography
                sx={{ fontWeight: 700, color: ui.text, lineHeight: 1.3, wordBreak: 'break-word' }}
              >
                {user.name || 'User'}
              </Typography>
              <Typography variant="body2" sx={{ color: ui.muted, wordBreak: 'break-all' }}>
                {user.email || ''}
              </Typography>
            </>
          )}
          <Chip
            size="small"
            label={`${progress}% complete`}
            sx={{
              mt: 0.75,
              fontWeight: 700,
              borderRadius: '8px',
              backgroundColor: clampProgress(progress) >= 100 ? ui.successSoft : ui.primarySoft,
              color: clampProgress(progress) >= 100 ? ui.success : ui.primaryDark,
            }}
          />
        </Box>
        {/* Toggle Button for Mobile */}
        <Tooltip title="Profile menu" arrow>
          <IconButton
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Open profile menu"
            sx={{
              flexShrink: 0,
              border: `1px solid ${ui.primary}`,
              borderRadius: '12px',
              color: ui.primary,
              '&:hover': { bgcolor: ui.primarySoft },
            }}
          >
            {isMenuOpen ? <CloseIcon sx={{ width: 24, height: 24 }} /> : <MenuIcon sx={{ width: 24, height: 24 }} />}
          </IconButton>
        </Tooltip>
      </Box>

      {/* Drawer for Mobile */}
      <Drawer
        variant="temporary"
        open={isMenuOpen}
        onClose={() => setIsMenuOpen(false)}
        sx={{
          display: { xs: 'block', lg: 'none' },
          '& .MuiDrawer-paper': {
            width: { xs: '88%', sm: 340 },
            maxWidth: 360,
            bgcolor: '#f4f7f9',
            p: 2,
            boxSizing: 'border-box',
          },
        }}
      >
        <ProfileContent
          progress={progress}
          imageUrl={profileImageUrl}
          handleImageChange={handleImageChange}
          navItems={navItems}
          onNavClick={handleNavClick}
          user={user}
          inputId="profile-upload-drawer"
          onClose={() => setIsMenuOpen(false)}
          activeSection={activeSection}
        />
      </Drawer>

      {/* Sidebar for Desktop */}
      <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
        <ProfileContent
          progress={progress}
          imageUrl={profileImageUrl}
          handleImageChange={handleImageChange}
          navItems={navItems}
          onNavClick={handleNavClick}
          user={user}
          inputId="profile-upload-desktop"
          activeSection={activeSection}
        />
      </Box>
    </>
  );
};

export default ProfileSidebar;